import AnalyticsEvent = ToroAnalyticsEnums.AnalyticsEvent;
import TurfProStatusGroup = ToroEnums.TurfProStatusGroup;

import { Component, OnInit } from '@angular/core';
import { finalize, take } from 'rxjs/operators';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { DashMessageService } from '../../../../../common/services/dash-message.service';
import { DashUserInfo } from '../../../../../api/dash-user/models/dash-user-info.model';
import { DashUserManagerService } from '../../../../../api/dash-user/dash-user-manager.service';
import { DashUserPreferences } from '../../../../../api/dash-user/models/dash-user-preferences.model';
import { DeviceManagerService } from '../../../../../common/services/device-manager.service';
import { environment } from '../../../../../../environments/environment';
import { SelectItem } from 'primeng/api';
import { ToroAnalyticsEnums } from '../../../../../common/enumerations/analytics.enums';
import { ToroDashboardWidget } from '../../toro-dashboard-widget';
import { ToroEnums } from '../../../../../common/enumerations/toro.enums';
import { ToroUtils } from '../../../../../common/utils/_toro.utils';
import { TranslateService } from '@ngx-translate/core';
import { TurfProManagerService } from '../../../../../api/turf-pro/turf-pro-manager.service';
import { TurfProRobot } from '../../../../../api/turf-pro/models/turf-pro-robot.type';
import { untilDestroyed } from '@ngneat/until-destroy';

export enum TpSortBy {
    Name = 'name',
    SerialNumber = 'serialNumber',
    Score = 'score',
    LastConnection = 'lastConnection',
    // InstallationDate = 'installationDate'
}

@Component({
    selector: 'toro-widget-turf-pro-asset-status',
    templateUrl: './widget-turf-pro-asset-status.component.html',
    styleUrls: ['./widget-turf-pro-asset-status.component.less']
})
export class WidgetTurfProAssetStatusComponent extends ToroDashboardWidget implements OnInit {

    iconColor = 'black';
    title = 'WIDGET.TURF_PRO_ASSET_STATUS';

    protected readonly maxModalGaugeHeightPx = 110;
    protected readonly gaugeLblVertOffsetMini = 29;
    protected readonly gaugeLblVertOffsetModal = 42;

    private _selectedSortKey: TpSortBy = TpSortBy.Name;
    protected set selectedSortKey(value: TpSortBy) {
        if (value == null) { return; }

        this._selectedSortKey = value;
        this.sortData();
    }

    protected get selectedSortKey(): TpSortBy {
        return this._selectedSortKey;
    }

    private _selectedFilterKey: TurfProStatusGroup = TurfProStatusGroup.Unknown;
    protected set selectedFilterKey(value: TurfProStatusGroup) {
        if (value == null) { return; }

        this._selectedFilterKey = value;
        this.filterData();
    }

    protected get selectedFilterKey(): TurfProStatusGroup {
        return this._selectedFilterKey;
    }

    protected showDesktopModal = false;
    protected isDemoMode = false;
    protected turfProAssets: TurfProRobot[] = [];
    protected filteredAndSortedAssets: TurfProRobot[] = [];
    protected statusActiveCount: number = 0;
    protected statusWaitingCount: number = 0;
    protected statusOffCount: number = 0;
    protected statusUnknownCount: number = 0;
    protected sortSelectList: SelectItem[] = [];
    protected filterSelectList: SelectItem[] = [];
    protected apiToken: string;
    protected newApiToken: string;
    protected showTokenDlg: boolean = false;

    private dashUserPrefs: DashUserPreferences;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(protected analyticsService: AnalyticsService,
                protected broadcastService: BroadcastService,
                protected dashMessageService: DashMessageService,
                protected dashUserManager: DashUserManagerService,
                protected deviceManager: DeviceManagerService,
                protected translateService: TranslateService,
                private turfProManager: TurfProManagerService
    ) {
        super(analyticsService, broadcastService, dashUserManager, deviceManager, translateService);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.isDemoMode = environment.isDemoMode;

        this.buildSelectLists();

        this.broadcastService.setupTurfProApiTokenChanged
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.getWidgetData(true);
            });
    }

    // =========================================================================================================================================================
    // Base Class Overrides
    // =========================================================================================================================================================

    get analyticsWidgetName(): string {
        return AnalyticsEvent.TurfProAssetStatus;
    }

    protected getWidgetData(isManualRefresh: boolean = false) {
        if (isManualRefresh) { this.isBusy = true; }

        this.dashUserManager.getDashUserInfo()
            .pipe(take(1))
            .subscribe({
                next: (dashUserInfo: DashUserInfo) => {
                    this.dashUserPrefs = dashUserInfo?.preferences;
                    if (!this.dashUserPrefs || this.dashUserPrefs.turfProApiToken == null || this.dashUserPrefs.turfProApiToken.trim() == '') {
                        this.setNoAccountDisplay();
                        return;
                    }

                    this.apiToken = this.dashUserPrefs.turfProApiToken;

                    this.turfProManager.getRobots()
                        .pipe(
                            take(1),
                            finalize(() => this.isBusy = false)
                        )
                        .subscribe({
                            next: (data: TurfProRobot[]) => {
                                this.turfProAssets = data;
                                this.setCounts();

                                this.filteredAndSortedAssets = data.slice();
                                this.filterData();

                                this.lastUpdateTimestamp = new Date();
                                this.clearIsUnableToFetchData();
                            },
                            error: err => {
                                if (err && err?.error?.Error_1[0].toLowerCase().includes('error retrieving data')) {
                                    this.setNoAccountDisplay();
                                    return;
                                }
                                this.dashMessageService.showWidgetDataFetchErrorMessage(this.title);
                            }
                        });
                },
                error: err => {
                    console.error(err);
                }
            });
    }

    private setNoAccountDisplay() {
        this.setIsUnableToFetchData('SPECIAL_MSG.PROVIDE_TURF_PRO_API_KEY');
    }

    protected setWidgetMenu() {
        super.setWidgetMenu();

        this.widgetMenuItems.unshift(
            {
                label: ToroUtils.Translate.instant('CASE_SENSITIVE.SET_ACCESS_TOKEN'),
                icon: 'pi pi-fw pi-key',
                command: this.showApiTokenDialog.bind(this)
            },
            { separator: true }
        );
    }

    private showApiTokenDialog() {
        this.newApiToken = this.apiToken;
        this.showTokenDlg = true;

        // const eventDetails = `API Key: ${this.mayaApiToken}`;
        // this.analyticsService.widgetEvent(AnalyticsEvent.MayaWidgetShowApiKeyDialog, AnalyticsCategory.Interaction, this.analyticsWidgetName, eventDetails);
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onLinkClick() {
        this.broadcastService.toggleSystemOverlay.next({ show: true, text: 'STRINGS.NAVIGATING_TO_TURF_PRO' });
        setTimeout(() => window.open(environment.turfProSiteUrl, '_blank'), 1000);
        setTimeout(() => this.broadcastService.toggleSystemOverlay.next({ show: false }), 2000);
    }

    onShowDesktopModal() {
        this.showDesktopModal = true;
    }

    onSetApiKey(apiToken: string) {
        this.showTokenDlg = false;

        // TODO: Temporary?
        if (apiToken != null) {
            this.newApiToken = apiToken;
        }

        // If the token was not changed, don't do anything.
        if (this.apiToken === this.newApiToken) { return; }

        this.apiToken = this.newApiToken;
        this.dashUserPrefs.turfProApiToken = this.apiToken;

        this.dashUserManager.updateDashUserPreferences(this.dashUserPrefs)
            .subscribe({
                next: () => {
                    if (this.apiToken != null && this.apiToken != '') {
                        this.isBusy = true;
                        // this.spinnerText = `${this.translateService.instant('STRINGS.RETRIEVING_DATA_FOR_NEW_API_TOKEN')}...`;
                        this.getWidgetData(true);
                    } else {
                        this.setNoAccountDisplay();
                    }

                    // const eventDetails = `Maya API Token: ${this.newMayaApiToken}`
                    // this.analyticsService.widgetEvent(AnalyticsEvent.MayaWidgetSaveApiKeyDialog, AnalyticsCategory.Interaction, this.analyticsWidgetName, eventDetails);
                },
                error: () => {
                    // this.resetBusy();
                    this.dashMessageService.showGenericSaveErrorMessage();
                }
            });
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private sortData() {
        if (this.turfProAssets?.length < 1) { return; }

        switch (this.selectedSortKey) {
            case TpSortBy.Name:
                this.filteredAndSortedAssets.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case TpSortBy.SerialNumber:
                this.filteredAndSortedAssets.sort((a, b) => a.serialNumber.localeCompare(b.serialNumber));
                break;
            case TpSortBy.Score:
                this.filteredAndSortedAssets.sort((a, b) => b.lastScore - a.lastScore);
                break;
            case TpSortBy.LastConnection:
                this.filteredAndSortedAssets.sort((a, b) => {
                    const dateA = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
                    const dateB = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
                    return dateB - dateA;
                });
                break;
            // case TpSortBy.InstallationDate:
            //     this.filteredAndSortedAssets.sort((a, b) => {
            //         const dateA = a.installationDate ? new Date(a.installationDate).getTime() : 0;
            //         const dateB = b.installationDate ? new Date(b.installationDate).getTime() : 0;
            //         return dateB - dateA;
            //     });
            //     break;
        }
    }

    private filterData() {
        if (this.turfProAssets?.length < 1) { return; }

        if (this.selectedFilterKey == TurfProStatusGroup.Unknown) {
            this.filteredAndSortedAssets = this.turfProAssets.slice();
        } else {
            this.filteredAndSortedAssets = this.turfProAssets.filter(a => a.statusGroup == this.selectedFilterKey);
        }

        this.sortData();
    }

    private setCounts() {
        this.statusOffCount = this.turfProAssets.filter(a => a.statusGroup == TurfProStatusGroup.Off).length;
        this.statusWaitingCount = this.turfProAssets.filter(a => a.statusGroup == TurfProStatusGroup.Waiting).length;
        this.statusActiveCount = this.turfProAssets.filter(a => a.statusGroup == TurfProStatusGroup.Active).length;
        this.statusUnknownCount = this.turfProAssets.filter(a => a.statusGroup == TurfProStatusGroup.Unknown).length;
    }

    private buildSelectLists() {
        this.sortSelectList = [
            { label: this.translateService.instant('STRINGS.NAME'), value: TpSortBy.Name },
            { label: this.translateService.instant('CASE_SENSITIVE.SERIAL_NUMBER'), value: TpSortBy.SerialNumber },
            { label: this.translateService.instant('STRINGS.SCORE'), value: TpSortBy.Score },
            { label: this.translateService.instant('CASE_SENSITIVE.LAST_CONNECTION'), value: TpSortBy.LastConnection },
            // { label: this.translateService.instant('CASE_SENSITIVE.INSTALLATION_DATE'), value: TpSortBy.InstallationDate }
        ];

        this.filterSelectList = [
            { label: this.translateService.instant('STRINGS.ALL'), value: TurfProStatusGroup.Unknown },
            { label: this.translateService.instant('STRINGS.OFF'), value: TurfProStatusGroup.Off },
            { label: this.translateService.instant('STRINGS.WAITING'), value: TurfProStatusGroup.Waiting },
            { label: this.translateService.instant('STRINGS.ACTIVE'), value: TurfProStatusGroup.Active }
        ];
    }
}
